import { createContext, useContext } from "react";
import { PortalScope, ThemeProps } from "../models/theme";

// What resolving an entry point can end in. Kept as four named states rather
// than a pair of booleans: "not found" and "expired" are different answers to
// a payer -- a link that has expired is actionable, a page that never existed
// is not -- and a union makes it impossible to be in both at once.
export const PORTAL_ENTRY_STATES = [
  "resolving",
  "resolved",
  "not-found",
  "expired",
  // Anything that is not the server's own answer: a 500, a timeout, a dropped
  // connection. It needs its own terminal state, because without one such a
  // failure sits at "resolving" forever and the payer watches a bare spinner
  // with no branding shell at all -- worse than the states that do resolve.
  "unavailable",
] as const;

export type PortalEntryState = (typeof PORTAL_ENTRY_STATES)[number];

export type PortalContextType = {
  // Null until an entry point resolves, and for the states that never will.
  scope: PortalScope | null;
  // Always a usable theme: the neutral default until a tenant's own resolves,
  // so the branding shell stands even when nothing else does.
  theme: ThemeProps;
  // The CORE id of the one service a payment link was made for. Not the id
  // a row of the service list carries, so it is matched, never navigated to.
  targetServiceId: string | null;
  // Where this visit began, so "back to the start" returns a payer to the
  // payment link or the page they came in by rather than to a guessed one.
  homePath: string;
  state: PortalEntryState;
};

const PortalContext = createContext<PortalContextType | undefined>(undefined);

export const usePortal = () => {
  const context = useContext(PortalContext);

  // No default value: a page reading an empty scope would look like a portal
  // that resolved to nothing rather than a provider that was never mounted.
  if (!context) {
    throw new Error("usePortal must be used within a PortalProvider");
  }

  return context;
};

export default PortalContext;
